import {
  adminSealMotorOilVars,
  defaultSealMotorOilVars,
  deleteSealMotorOils,
  ecopetrolSealMotorOilVars,
  kocSealMotorOilVars,
  restoreSealMotorOils
} from "@/models/equipment/seal_motor_oil/predicates";
import type { Seal_Motor_Oil } from "gql/api";
import {
  useInsertSealMotorOilMutation,
  useSealMotorOilQuery,
  useUpdateSealMotorOilMutation,
  useDeleteSealMotorOilsMutation,
  useRestoreSealMotorOilsMutation
} from "gql/api";
import { computed } from "vue";
import { v4 } from "uuid";
import { useEquipment } from "@/composables/equipment/baseEquipment";
import type { UseEquipment } from "@/composables/equipment/equipmentTypes";
import { sealMotorOilsTableColumns } from "./sealMotorOilsTableColumns";
import useUserInfo from "@/composables/auth/useUserInfo";

export function useSealMotorOils(
  isAdmin: boolean
): UseEquipment<Seal_Motor_Oil> {
  const baseEquipment = useEquipment<Seal_Motor_Oil>(isAdmin);
  const title = "Seal Motor Oils";
  const user = useUserInfo();

  const getVariables = () => {
    if (user.is_koc_customer) return kocSealMotorOilVars;
    if (user.is_ecopetrol_customer) return ecopetrolSealMotorOilVars;
    if (user.is_admin) return adminSealMotorOilVars;
    return defaultSealMotorOilVars;
  };

  const {
    data,
    executeQuery: refetch,
    stale: isStale
  } = useSealMotorOilQuery({
    variables: getVariables()
  });

  const allRows = computed(
    () => data.value?.seal_motor_oil as Seal_Motor_Oil[] | undefined
  );

  const hashOrder: (keyof Seal_Motor_Oil)[] = ["name"];

  const { defineVisibleColumns, defineFilterData } = baseEquipment;
  const { defineUpsert, defineRestore, defineDelete } =
    baseEquipment.defineMutationHandlers(refetch, hashOrder);

  const { executeMutation: executeInsert } = useInsertSealMotorOilMutation();
  const { executeMutation: executeUpdate } = useUpdateSealMotorOilMutation();
  const { executeMutation: executeDelete } = useDeleteSealMotorOilsMutation();
  const { executeMutation: executeRestore } = useRestoreSealMotorOilsMutation();

  const insertOil = (oil: Seal_Motor_Oil) => executeInsert({ object: oil });
  const updateOil = (id: string, oil: Seal_Motor_Oil) =>
    executeUpdate({ id, object: oil });
  const deleteOil = (ids: string[]) =>
    executeDelete({ where: deleteSealMotorOils(ids) });
  const restoreOil = (ids: string[]) =>
    executeRestore({ where: restoreSealMotorOils(ids) });

  baseEquipment.defineEmptyRow(() => ({
    __typename: "seal_motor_oil",
    id: v4(),
    created_at: "now()",
    updated_at: "now()",
    deleted_at: null,
    name: "",
    thermal_expansion_factor: null,
    max_temp_fahrenheit: null,
    mf_test_temp1_celsius: null,
    mf_test_temp2_celsius: null,
    viscosity_test1_centistroke: null,
    viscosity_test2_centistroke: null,
    specific_gravity: null
  }));

  return {
    searchPlaceholder: "Search Motor Oils…",
    columns: defineVisibleColumns(sealMotorOilsTableColumns),
    handleUpsert: defineUpsert(insertOil, updateOil),
    handleDelete: defineDelete(deleteOil),
    handleRestore: defineRestore(restoreOil),
    totalCount: computed(
      () => data.value?.seal_motor_oil_aggregate.aggregate?.count
    ),
    title,
    allRows,
    isAdmin,
    refetch,
    isStale,
    ...baseEquipment,
    ...defineFilterData({
      columns: sealMotorOilsTableColumns,
      globalSearchFields: ["name"],
      allRows,
      title
    })
  };
}
